import React, { useState } from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import emailjs from "emailjs-com";
import SEOHead from "../components/common/SEOHead";

const ContactPremium = () => {
  const [formData, setFormData] = useState({
    from_name: "",
    reply_to: "",
    to_name: "Livneu",
    subject: "",
    message: "",
    inquiry_type: "",
  });
  const [isSending, setIsSending] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const seoData = {
    title: "Contact Livneu - Get in Touch with Our VTuber Agency",
    description:
      "Contact Livneu for partnerships, inquiries, or support. Reach out to Sri Lanka's premier VTuber agency for professional virtual entertainment services.",
    image: "https://livneu.levelstair.com/ShareImage.png",
    url: "https://livneu.levelstair.com/contact",
    keywords:
      "contact Livneu, VTuber agency contact, LEVELSTAIR contact, virtual talent inquiries, partnership requests",
  };

  const inquiryTypes = [
    "General Inquiry",
    "Partnership/Collaboration",
    "Media/Press",
    "Technical Support",
    "Talent Application",
    "Business Development",
    "Fan/Community Question",
    "Other",
  ];

  const contactMethods = [
    {
      icon: "mail-outline",
      title: "Email",
      description: "levelstair+livneu@gmail.com",
      action: "mailto:levelstair+livneu@gmail.com",
      color: "from-blue-500 to-indigo-600",
    },
    {
      icon: "logo-discord",
      title: "Discord",
      description: "Join our community server",
      action: "https://discord.com/invite/xauShxdx6u",
      color: "from-indigo-500 to-purple-600",
    },
    {
      icon: "logo-whatsapp",
      title: "WhatsApp",
      description: "Get instant updates",
      action: "https://chat.whatsapp.com/HlqjZtbFtU6ISAEMYBxVRz",
      color: "from-green-500 to-emerald-600",
    },
    {
      icon: "business-outline",
      title: "Business",
      description: "Partnership inquiries",
      action: "mailto:levelstair+livneu@gmail.com?subject=Business Inquiry",
      color: "from-purple-500 to-pink-600",
    },
  ];

  const faqItems = [
    {
      question: "How do I apply to become a VTuber with Livneu?",
      answer:
        "Visit our Apply page and fill out the comprehensive application form. We review all applications within 5-7 business days.",
    },
    {
      question: "What equipment do I need to get started?",
      answer:
        "Basic requirements include a quality microphone, webcam, stable internet connection, and a computer capable of running streaming software.",
    },
    {
      question: "Do you accept international talent?",
      answer:
        "Yes! While we're based in Sri Lanka, we welcome talented individuals from around the world who share our vision.",
    },
    {
      question: "What support do you provide to your talent?",
      answer:
        "We offer comprehensive support including avatar design, technical assistance, content strategy, audience growth, and monetization guidance.",
    },
  ];

  // Handle Form Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Send Email
  const sendEmail = (e) => {
    e.preventDefault();
    setIsSending(true);

    emailjs
      .sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        e.target,
        process.env.REACT_APP_EMAILJS_USER_ID
      )
      .then(
        (result) => {
          setShowSuccessMessage(true);
          setFormData({
            from_name: "",
            to_name: "LIVNEU",
            email: "",
            subject: "",
            message: "",
            inquiry_type: "",
          });
          setIsSending(false);
          // Hide success message after 5 seconds
          setTimeout(() => setShowSuccessMessage(false), 5000);
        },
        (error) => {
          alert(
            "Sorry, we've reached our email limit for this month. Please send your message directly to: levelstair+livneu@gmail.com"
          );
          console.error("EmailJS Error:", error);
          setIsSending(false);
        }
      );
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen">
        <SEOHead seoData={seoData} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-contact">
          {/* Kawaii Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-pastel-purple/15 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-pastel-blue/15 rounded-full blur-3xl animate-pulse"></div>

            {/* Floating Kawaii Particles */}
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-pastel-purple/60 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [-15, -30, -15],
                  opacity: [0.3, 1, 0.3],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>

          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-7xl font-title text-text-primary mb-6 leading-tight kawaii-glow">
                Get in{" "}
                <span className="text-text-accent">
                  Touch
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-text-secondary mb-8 leading-relaxed font-body">
                Have questions about Livneu? Want to explore
                partnerships? We'd love to hear from you.
              </p>

              <p className="text-lg text-text-accent font-accent font-medium">
                Let's build the future of virtual entertainment together.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Multiple Ways to Connect
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Choose the method that works best for you
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mb-16">
              {contactMethods.map((method, index) => (
                <motion.a
                  key={index}
                  href={method.action}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  whileHover={{ y: -10, scale: 1.05 }}
                  className="glass-card p-8 text-center group no-underline smooth-transition"
                >
                  <div
                    className={`w-16 h-16 bg-gradient-to-br ${method.color} rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <ion-icon
                      name={method.icon}
                      style={{ fontSize: "2rem", color: "white" }}
                    ></ion-icon>
                  </div>

                  <h3
                    className="text-xl font-bold text-black mb-4"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {method.title}
                  </h3>

                  <p
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {method.description}
                  </p>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Send Us a Message
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                We'll get back to you within 24 hours
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              {showSuccessMessage && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-8 text-center"
                >
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ion-icon
                      name="checkmark-outline"
                      style={{ fontSize: "1.5rem", color: "white" }}
                    ></ion-icon>
                  </div>
                  <p
                    className="text-green-400 font-semibold text-lg"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Message sent successfully! We'll get back to you soon.
                  </p>
                </motion.div>
              )}

              <form onSubmit={sendEmail} className="glass-card p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Left Column */}
                  <div className="space-y-6">
                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="from_name"
                        value={formData.from_name}
                        onChange={handleChange}
                        required
                        className="glass-input w-full px-6 py-4"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="glass-input w-full px-6 py-4"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Inquiry Type
                      </label>
                      <select
                        name="inquiry_type"
                        value={formData.inquiry_type}
                        onChange={handleChange}
                        className="glass-input w-full px-6 py-4"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        <option value="" className="text-gray-900">
                          Select inquiry type
                        </option>
                        {inquiryTypes.map((type) => (
                          <option
                            key={type}
                            value={type}
                            className="text-gray-900"
                          >
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="glass-input w-full px-6 py-4"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        placeholder="Brief subject of your message"
                      />
                    </div>

                    <div>
                      <label
                        className="block text-black font-semibold mb-3 text-lg"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="8"
                        className="glass-input w-full px-6 py-4 resize-none"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                        placeholder="Tell us more about your inquiry..."
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="mt-12 text-center">
                  <motion.button
                    type="submit"
                    disabled={isSending}
                    whileHover={{
                      scale: isSending ? 1 : 1.05,
                      boxShadow: "0 25px 50px rgba(151, 46, 240, 0.4)",
                    }}
                    whileTap={{ scale: 0.95 }}
                    className={`glass-button px-12 py-5 text-xl ${
                      isSending ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {isSending ? "Sending Message..." : "Send Message"}
                  </motion.button>

                  <p
                    className="text-gray-700 mt-6"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    We typically respond within 24 hours during business days.
                  </p>
                </div>
              </form>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Frequently Asked Questions
              </h2>
              <p
                className="text-xl text-gray-700 max-w-3xl mx-auto"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Quick answers to common questions
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto">
              {faqItems.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="mb-8 glass-card p-8 smooth-transition"
                >
                  <h3
                    className="text-xl font-bold text-black mb-4"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {faq.question}
                  </h3>
                  <p
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-center mt-12"
            >
              <p
                className="text-lg text-gray-700 mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Don't see your question here?
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() =>
                  document
                    .querySelector("form")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="glass-button px-8 py-4 text-lg"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Ask Us Directly
              </motion.button>
            </motion.div>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
};

export default ContactPremium;
